import React, { useState } from "react";
import { LuCheck, LuChevronDown, LuInfo } from "react-icons/lu";
import { Badge, cn } from "../../../components/ui";
import { t } from "../../../i18n";
import { OpmeBundle, ProcedimentoBundle } from "../../../types/resources.types";
import { ReferenceItem, referenceUrl } from "../../references";
import { AppealWizard } from "../useAppealWizard";

/** Passo 6: o médico confere as referências (Incluir/Remover, abrir cada link) e assina a declaração. */
export const TechnicalReviewStep: React.FC<{ w: AppealWizard }> = ({ w }) => {
  const [open, setOpen] = useState<Set<string>>(() => new Set([w.procedimentos[0]?.nome ?? w.opmes[0]?.nome]));

  const toggleOpen = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const section = (title: string, bundles: (ProcedimentoBundle | OpmeBundle)[]) =>
    bundles.length > 0 && (
      <section className="flex flex-col gap-2.5">
        <h3 className="text-label font-semibold text-fg-secondary">{title}</h3>
        {bundles.map((b) => {
          const isOpen = open.has(b.nome);
          const selected = b.referencias.filter((r) => w.selectedRefs[r.id]).length;
          return (
            <div key={b.nome} className="rounded-control border border-line bg-surface">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggleOpen(b.nome)}
                className="flex w-full items-start gap-2.5 p-3.5 text-left focus-visible:outline-none focus-visible:shadow-focus"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <strong className="break-words text-body font-semibold">{b.nome}</strong>
                  {b.justificativa_relacao_com_caso && <span className="text-caption text-fg-subtle">{b.justificativa_relacao_com_caso}</span>}
                  <span>
                    <Badge tone={selected ? "primary" : "neutral"}>
                      {t("references.review.itemSelected", { selected, total: b.referencias.length })}
                    </Badge>
                  </span>
                </span>
                <LuChevronDown size={20} aria-hidden className={cn("mt-0.5 shrink-0 text-fg-subtle transition-transform duration-fast", isOpen && "rotate-180")} />
              </button>
              {isOpen && (
                <div className="flex flex-col gap-2.5 px-3.5 pb-3.5">
                  {b.referencias.map((ref) => {
                    const url = referenceUrl(ref);
                    return (
                      <ReferenceItem
                        key={ref.id}
                        item={ref}
                        selected={!!w.selectedRefs[ref.id]}
                        opened={!!url && w.openedUrls.has(url)}
                        onToggle={w.toggleRef}
                        onOpen={w.markOpened}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </section>
    );

  const check = (ok: boolean, label: string) => (
    <span className={cn("inline-flex items-center gap-1.5", ok ? "text-success" : "text-fg-muted")}>
      {ok ? <LuCheck size={15} aria-hidden /> : <LuInfo size={15} aria-hidden />}
      {label}
    </span>
  );

  return (
    <>
      {w.hasRefs ? (
        <>
          <p className="text-body text-fg-muted">{t("appeals.review.intro")}</p>
          {section(t("appeals.generated.procedures"), w.procedimentos)}
          {section(t("appeals.generated.opme"), w.opmes)}
          <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-caption" aria-live="polite">
            {check(w.selectedRefList.length > 0, t("references.review.atLeastOne"))}
            {check(w.selectedRefList.length > 0 && w.openedCount === w.linkCount, t("references.review.linksChecked", { opened: w.openedCount, total: w.linkCount }))}
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2.5 rounded-control bg-surface-muted p-3.5 text-body">
          <LuInfo size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden />
          <p>
            <strong className="block font-semibold">{t("appeals.review.noRefsTitle")}</strong>
            <span className="text-fg-muted">{t("appeals.review.noRefsBody")}</span>
          </p>
        </div>
      )}
      <label className="flex cursor-pointer items-start gap-2.5 rounded-control border border-primary-line bg-primary-tint px-3 py-2.5 text-caption text-fg-secondary">
        <input type="checkbox" checked={w.declared} onChange={w.toggleDeclared} className="mt-0.5 h-5 w-5 shrink-0 accent-[rgb(var(--primary))]" />
        {t("appeals.review.declaration")}
      </label>
    </>
  );
};
